/**
 * Single client-side helper for the paywall_cta_clicked event.
 *
 * Fire from any paywall surface (modal, inline cap-error, quota banner) so all
 * upgrade-intent signal lands in the same event under event_log, sliced by
 * `surface` + `action`. The audit found this signal was the #1 missing piece of
 * the funnel — instrumenting it everywhere lets /admin/north-star compute a
 * real "daily-cap upgrade CTA click rate" instead of the current "—" placeholder.
 *
 * Delivery goes through `emitClientEvent`, which attaches the visitor's
 * `clientId` + `sessionId` and prefers sendBeacon so the request survives the
 * navigation these clicks almost always trigger.
 */

import { emitClientEvent } from '@/lib/analytics/emit-client-event';

export interface TrackPaywallCtaParams {
  /** Surface key; must match PAYWALL_SURFACES in /api/events/conversion/route.ts. */
  surface:
    | 'daily_cap'
    | 'monthly_cap'
    | 'trial_exhausted'
    /** Canonical — matches admission.ts's paywall_hit reason. */
    | 'file_duration_limit'
    /** Legacy alias, kept for historical rows. Prefer 'file_duration_limit'. */
    | 'file_too_long'
    | 'quota_blocked'
    // Transcript-detail gates: the locked-feature click IS the CTA click — the
    // user is routed straight to /pricing. Without these, the dominant paywall
    // surfaces report hits but zero clicks (the 2026-07 "50 hits / 0 clicks"
    // blind spot).
    | 'ai_tab'
    | 'export_format'
    | 'translate'
    /** 2026-09 COMEBACK40 win-back campaign banner on the dashboard. */
    | 'comeback_banner';
  action: 'upgrade' | 'claim_bonus' | 'buy_pack';
  plan?: string;
  transcription_id?: number;
}

export function trackPaywallCtaClicked(opts: TrackPaywallCtaParams): void {
  emitClientEvent('/api/events/conversion', { name: 'paywall_cta_clicked', ...opts });
}

export interface TrackPricingCtaParams {
  /** Which pricing CTA: hero buttons on /pricing, or a plan card (either pricing page). */
  cta: 'hero_primary' | 'hero_secondary' | 'plan_card';
  /** Plan id for plan-card clicks ('pro' / 'studio' / 'agency' / 'enterprise' / 'free'). */
  plan?: string;
  /** Origin surface that sent the user here (the ?from= param, e.g. 'ai_tab', 'export', 'translate'). */
  from?: string;
  /** Pathname where the click happened ('/pricing' vs '/dashboard/pricing'). */
  page?: string;
  /**
   * Billing-toggle state at click time ('annual' | 'monthly'). Without it the
   * annual-first experiment is unreadable — checkout events carry interval but
   * the pricing-page step between paywall and checkout did not.
   */
  interval?: 'annual' | 'monthly';
}

/**
 * Middle-of-funnel signal between paywall_hit/paywall_cta_clicked and
 * checkout_started. Without it the pricing page is a black box — we see users
 * arrive (paywall_cta_clicked) and occasionally exit to Polar (checkout_started)
 * with nothing in between.
 */
export function trackPricingCtaClicked(opts: TrackPricingCtaParams): void {
  emitClientEvent('/api/events/conversion', { name: 'pricing_cta_clicked', ...opts });
}
